/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE. 
*/

"use client";

import { useCosmosComponentContext } from "@cosmos/components";
import { PageSearchFragment } from "@cosmos/hygraph-schema";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useSearch } from "../hooks";

import { SearchSection } from "./Search.section";

export type SearchPageProps = PageSearchFragment & {
	locale: string;
};

export const SearchPage = ({
	searchFilters,
	locale,
	slug,
	defaultSearchFilter,
	...props
}: SearchPageProps): React.JSX.Element => {
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();

	const queryParam = searchParams.get("keyword") || "";
	const [query, setQuery] = useState(queryParam);

	const pageParam = parseInt(searchParams.get("page") || "1");
	const [currentPage, setCurrentPage] = useState<number>(pageParam > 1 ? pageParam : 1);

	const filterParam = searchParams.get("filter") || "";
	const [currentFilter, setFilter] = useState(filterParam);

	const [paramsPath, setParamsPath] = useState<string | undefined>();
	const [noFilterPath, setNoFilterPath] = useState("");

	const { LoadingComponent } = useCosmosComponentContext();

	const handleFilterChange = useCallback(
		(newFilter: string) => {
			setFilter((currentFilter) => {
				if (newFilter !== currentFilter) setCurrentPage(1);
				return newFilter;
			});
		},
		[setFilter, setCurrentPage],
	);

	const handleQueryChange = useCallback(
		(newQuery: string) => {
			setQuery((query) => {
				if (newQuery !== query) setCurrentPage(1);
				return newQuery;
			});
		},
		[setQuery, setCurrentPage],
	);

	useEffect(() => {
		if (typeof filterParam === "string") handleFilterChange(filterParam);
		if (typeof queryParam === "string") handleQueryChange(queryParam);
		if (pageParam > 0) setCurrentPage(pageParam);
	}, [queryParam, filterParam, pageParam, handleQueryChange, handleFilterChange, setCurrentPage]);

	const defaultFilter = useMemo(
		() => searchFilters?.find((filter) => filter.value === defaultSearchFilter)?.label || "",
		[searchFilters, defaultSearchFilter],
	);

	useEffect(() => {
		const params = new URLSearchParams(searchParams);

		params.set("keyword", query);
		if (currentPage > 1) {
			params.set("page", `${currentPage}`);
		} else params.delete("page");

		if (currentFilter?.length && currentFilter !== defaultFilter) {
			params.set("filter", currentFilter);
		} else params.delete("filter");

		const newParams = params.toString();
		setParamsPath(newParams);

		params.delete("filter");
		setNoFilterPath(`${pathname}?${params.toString()}`);
	}, [searchParams, currentPage, currentFilter, query, defaultFilter, pathname, setParamsPath, setNoFilterPath]);

	useEffect(() => {
		if (typeof paramsPath === "string") router.push(`${pathname}?${paramsPath}`);
	}, [router, pathname, paramsPath]);

	const { response: searchResults, isLoading } = useSearch(query, locale, currentFilter, slug);

	useEffect(() => {
		if (!isLoading && searchResults?.total && (currentPage - 1) * 10 > searchResults?.total) setCurrentPage(1);
	}, [isLoading, searchResults?.total, currentPage, setCurrentPage]);

	return (
		<SearchSection
			{...props}
			searchFilters={searchFilters}
			onFilterChange={handleFilterChange}
			onPageChange={setCurrentPage}
			currentFilter={currentFilter}
			defaultFilter={defaultFilter}
			searchValue={query}
			searchResults={searchResults}
			isLoading={isLoading}
			onValueChange={handleQueryChange}
			currentPage={currentPage}
			noFilterPath={noFilterPath}
			loadingComponent={<LoadingComponent />}
		></SearchSection>
	);
};
